import { RRule, Weekday as RRuleWeekday } from "rrule";
import type { Booking } from "@/types/Booking";
import type { Recurrence } from "@/types/Recurrence";
import { formatUtcDate, humaniseDateFriendly } from "@/lib/utils";
import { sameUtcDay, utcDayBounds, utcDayKey, utcWeekday } from "@/lib/calendarDay";

const WEEKDAY_MAP: Record<string, RRuleWeekday> = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
};

const WEEKDAYS_BY_INDEX: Array<"SU" | "MO" | "TU" | "WE" | "TH" | "FR" | "SA"> = [
  "SU",
  "MO",
  "TU",
  "WE",
  "TH",
  "FR",
  "SA",
];

/**
 * Returns the recurrence schedule that should drive occurrence math for a booking.
 *
 * R13 (BOOKING_RULES.md#r13) — a slot booking is *indefinite weekly* by definition. Any persisted
 * recurrence document on a slot is normalized at read time: we keep `byWeekday` and `exceptions`
 * but always force `frequency: "weekly"`, `interval: 1`, and strip `count`/`until`. This guards
 * against two real bugs seen in production:
 *   1. Slot rows whose `recurrence` field is missing (legacy data, or sync-imported via OfficeRND
 *      where the RRule failed to parse) — synthesised from the genesis weekday.
 *   2. ORND-imported slots whose RRule carries an UNTIL or COUNT (e.g.
 *      `FREQ=WEEKLY;BYDAY=TU;UNTIL=20260714T093000Z`). Without normalization the slot silently
 *      disappears from the calendar past UNTIL even though per R13 slots run forever.
 *
 * Cancellation paths: full cancellation flips `booking.isCancelled` (filtered upstream by every
 * calendar surface); per-occurrence cancellations land in `recurrence.exceptions` (R17) and are
 * preserved through normalization.
 *
 * Non-slot bookings keep their persisted recurrence as-is — adhoc-recurring bookings stay bounded
 * by their `count`/`until` per R12. Returns `null` when the booking is genuinely non-recurring.
 */
/**
 * Whether a booking should be treated as repeating for UI purposes (R13 — slots are
 * intrinsically weekly even if their persisted recurrence is absent, e.g. ORND imports or
 * legacy data). Use this instead of `booking.recurrence || booking.recurrencePattern` for
 * any "is this booking part of a series?" decision (R pills, repeat info cards, tooltips).
 */
export function isBookingRepeating(booking: Booking): boolean {
  return booking.type === "slot" || !!(booking.recurrence || booking.recurrencePattern);
}

/**
 * Number of occurrences to enumerate when listing a booking's series (driven by the
 * "Manage series" panel via BookingOccurrenceList.enumerateOccurrences). Mirrors the
 * indefinite-vs-bounded split the rest of recurrence math uses:
 *   • Slots — R13 indefinite weekly → 52.
 *   • Recurrence with persisted `count` — honour it.
 *   • Recurrence with no `count` and no `until` — treat as indefinite → 52. This covers
 *     the "rolling weekly adhoc" shape (base or instance) where the linear enumerator
 *     would otherwise fall back to a 12-occurrence default and miss the future entirely
 *     once the genesis date drifts >12 intervals into the past.
 *   • Recurrence bounded by `until` only — fall back to 12 (the linear enumerator can't
 *     consume `until` directly; 12 is a safety preview).
 */
export function getSeriesOccurrenceCount(booking: Booking): number {
  if (booking.type === "slot") return 52;
  const rec = booking.recurrence ?? booking.recurrencePattern;
  if (rec?.count != null) return rec.count;
  if (rec?.until == null) return 52;
  return 12;
}

/**
 * Whether the "Manage series" panel should be shown for this booking. Stricter than
 * `isBookingRepeating`: a recurrence stub that produces only a single occurrence (e.g. an
 * adhoc with `count: 1`, or one whose `until` lands on the genesis date) is technically
 * "repeating" but has no series to manage. Slots are always shown (R13 — indefinite weekly).
 * For bounded recurrences we require both more-than-one total occurrence AND at least one
 * occurrence in the future, since past occurrences are read-only.
 */
export function hasManageableSeriesOccurrences(booking: Booking, now: Date = new Date()): boolean {
  if (!isBookingRepeating(booking)) return false;
  if (booking.type === "slot") return true;
  const occurrences = expandRecurrence(booking);
  if (occurrences.length <= 1) return false;
  // "Today" is the centre's (UTC) calendar day, not the viewer's local day — a Rome viewer at
  // 00:30 local must see the same centre-day state as a London viewer. occurrences here are
  // UTC-instant (see buildRule's UTC-anchored dtStart), so floor both sides via utcDayBounds
  // rather than date-fns' local `startOfDay`.
  const todayStart = utcDayBounds(now).start.getTime();
  return occurrences.some((d) => utcDayBounds(d).start.getTime() >= todayStart);
}

export function getEffectiveRecurrence(booking: Booking): Recurrence | null {
  if (booking.type === "slot") {
    const existing = booking.recurrence ?? booking.recurrencePattern;
    const genesis = booking.date instanceof Date ? booking.date : new Date(booking.date);
    const byWeekday =
      existing?.byWeekday && existing.byWeekday.length > 0
        ? existing.byWeekday
        : [WEEKDAYS_BY_INDEX[utcWeekday(genesis)]];
    // R13: ignore the raw `until` (ORND-import junk). A deliberate cancellation sets
    // `cancelledFrom` (the first non-occurring date) — honour it by capping the rule one
    // instant before that day, so occurrences on/after cancelledFrom stop.
    const cancelledFrom = existing?.cancelledFrom ? new Date(existing.cancelledFrom) : null;
    const until = cancelledFrom ? new Date(utcDayBounds(cancelledFrom).start.getTime() - 1) : undefined;
    return {
      baseBookingId: existing?.baseBookingId ?? booking.id,
      frequency: "weekly",
      interval: 1,
      byWeekday,
      until,
      exceptions: existing?.exceptions,
    } as Recurrence;
  }
  const source = booking.recurrence ?? booking.recurrencePattern;
  if (!source) return null;
  // A from-date cancellation is a hard stop for non-slots too. It normally also writes a matching
  // `until`, but anything that later rewrites `until` (the org-deactivation cascade did) must not
  // bring cancelled occurrences back — cap at whichever bound is earlier, as ember's occursOnDate does.
  if (!source.cancelledFrom) return source;
  const cancelledUntil = new Date(utcDayBounds(new Date(source.cancelledFrom)).start.getTime() - 1);
  const rawUntil = source.until ? new Date(source.until) : null;
  if (rawUntil && rawUntil.getTime() <= cancelledUntil.getTime()) return source;
  return { ...source, until: cancelledUntil };
}

/**
 * Builds the rrule.RRule that drives occurrence math for the given booking. Centralises the
 * dtstart calculation + frequency mapping so the four exported helpers stay in step.
 */
function buildRule(booking: Booking, recurrence: Recurrence): RRule {
  const bookingDate = booking.date instanceof Date ? booking.date : new Date(booking.date);
  const dateStr = utcDayKey(bookingDate);
  // The `T${HH}:${mm}` suffix is parsed as UTC (trailing `Z`) so the resulting instant keeps
  // the occurrence on the SAME UTC calendar day as `dateStr` regardless of the viewer's browser
  // timezone — rrule computes weekday/day in UTC, so a local (no-`Z`) parse here would drift the
  // enumerated day at extreme offsets (e.g. a 09:00 start lands on the previous UTC day at +14).
  // The booking's `startTime` is treated as the centre/UTC time, consistent with
  // `buildRuleForDateCheck` below, which already anchors to UTC midnight.
  const dtStart = new Date(`${dateStr}T${booking.startTime.slice(0, 2)}:${booking.startTime.slice(2)}:00Z`);

  return new RRule({
    freq:
      recurrence.frequency === "daily"
        ? RRule.DAILY
        : recurrence.frequency === "weekly"
          ? RRule.WEEKLY
          : RRule.MONTHLY,
    interval: recurrence.interval ?? 1,
    count: recurrence.count,
    until: recurrence.until ? new Date(recurrence.until) : undefined,
    byweekday: recurrence.byWeekday ? recurrence.byWeekday.map((day) => WEEKDAY_MAP[day]) : undefined,
    // For monthly "nth weekday" recurrence (e.g., "3rd Monday"), use bysetpos
    bysetpos: recurrence.bySetPos,
    dtstart: dtStart,
  });
}

/**
 * Like buildRule but uses UTC midnight (derived from the LOCAL date string) as dtStart instead
 * of the booking's actual startTime. Used exclusively by occursOnDate, which only needs to know
 * WHICH calendar day an occurrence falls on — not its exact time.
 *
 * WHY this matters: `buildRule` constructs dtStart from a local datetime string
 * (e.g. "2026-06-22T00:00:00"). In a UTC+1 timezone (BST), midnight local = 23:00 UTC the
 * *previous* day, so dtStart is on a Sunday in UTC even though the booking is on a Monday. rrule
 * computes day-of-week in UTC, so it then fires "Monday" occurrences at 23:00 UTC, which appear
 * as Tuesday 00:00 BST — outside the UTC day window for Monday. Deriving dateStr via `utcDayKey`
 * (the booking's stored UTC calendar day) and parsing it as a date-only ISO string (UTC midnight
 * per ECMAScript spec) keeps the dtStart firmly on the correct UTC calendar day — the centre's day
 * — so rrule's BYDAY math aligns regardless of the viewer's browser timezone.
 */
function buildRuleForDateCheck(booking: Booking, recurrence: Recurrence): RRule {
  const bookingDate = booking.date instanceof Date ? booking.date : new Date(booking.date);
  const dateStr = utcDayKey(bookingDate);
  // Date-only ISO strings ("yyyy-MM-dd") are parsed as UTC midnight by spec — the result is
  // always on the booking's correct UTC calendar day regardless of the viewer's timezone.
  const dtStart = new Date(dateStr);

  return new RRule({
    freq:
      recurrence.frequency === "daily"
        ? RRule.DAILY
        : recurrence.frequency === "weekly"
          ? RRule.WEEKLY
          : RRule.MONTHLY,
    interval: recurrence.interval ?? 1,
    count: recurrence.count,
    until: recurrence.until ? new Date(recurrence.until) : undefined,
    byweekday: recurrence.byWeekday ? recurrence.byWeekday.map((day) => WEEKDAY_MAP[day]) : undefined,
    bysetpos: recurrence.bySetPos,
    dtstart: dtStart,
  });
}

export function expandRecurrence(booking: Booking): Date[] {
  const recurrence = getEffectiveRecurrence(booking);
  if (!recurrence) return [];

  const rule = buildRule(booking, recurrence);

  // Unbounded recurrences (slot bookings recur forever) have neither a `count` nor an
  // `until`. `rule.all()` on such a rule never terminates, so we cap the enumeration with
  // a 1-year window starting at the genesis date. Bounded recurrences (count/until) keep
  // their full expansion via `rule.all()` so callers see every scheduled occurrence.
  let dates: Date[];
  if (recurrence.count == null && recurrence.until == null) {
    const genesis = booking.date instanceof Date ? booking.date : new Date(booking.date);
    const horizon = new Date(genesis);
    // deferred: time-of-day instant, not day-identity — a coarse +1-year window bound; the
    // local getFullYear/setFullYear round-trip shifts genesis by exactly one calendar year
    // regardless of viewer timezone.
    horizon.setFullYear(horizon.getFullYear() + 1);
    dates = rule.between(genesis, horizon, true);
  } else {
    dates = rule.all();
  }

  const exceptions = recurrence.exceptions?.map((d) => utcDayKey(new Date(d))) || [];
  return dates.filter((date) => !exceptions.includes(utcDayKey(date)));
}

/**
 * The final occurrence date of a BOUNDED, non-slot recurring booking — i.e. one whose recurrence
 * carries a `count` or an `until`. Returns null for:
 *   • slots (R13 — indefinite weekly, never "end"),
 *   • unbounded recurrences (no count, no until — a rolling weekly adhoc has no end), and
 *   • genuinely non-recurring bookings.
 * Drives the "Ending/Ended {date}" line on the booking views.
 */
export function getRecurrenceEndDate(
  booking: Booking,
  membershipEndDate?: Date | string | null,
): Date | null {
  const recurrence = getEffectiveRecurrence(booking);
  if (!recurrence) return null;

  // Defense-in-depth for a queued (pending) membership end: the member's series must not be shown as
  // running past their membership end date, even when the booking's own recurrence was never truncated
  // (legacy data, a non-cascade scheduling path, or a later edit that stripped `until`). Callers pass
  // `member.membershipEndDate` only when the member is pending termination. The end date is INCLUSIVE
  // (the member is active through that whole day), so treat it as an `until` at end-of-day.
  const membershipUntil = membershipEndDate ? utcDayBounds(new Date(membershipEndDate)).end : null;

  // Slots first: getEffectiveRecurrence deliberately drops a slot's raw `until` (R13 — ORND junk)
  // and gives slots no count, so the unbounded guard below would treat every slot as never-ending.
  // A slot DOES end when it has been bounded — end-membership truncation sets a raw recurrence.until
  // (the termination date), and a slot cancellation sets cancelledFrom (surfaced as the effective
  // `until`). Surface whichever exists — or the membership end — so a pending-termination member's
  // slot shows its end date (previously slots returned null unconditionally even though the calendar
  // stopped there).
  if (booking.type === "slot") {
    const boundedRaw = booking.recurrence?.until ?? booking.recurrencePattern?.until ?? recurrence.until;
    const slotUntil = boundedRaw != null ? new Date(boundedRaw) : null;
    const effective = earliestDate(slotUntil, membershipUntil);
    if (!effective) return null;
    return lastOccurrenceOnOrBefore(booking, recurrence, effective);
  }

  // A non-slot series can be bounded four ways: `count`, an explicit `until`, a from-date cancellation
  // (`cancelledFrom`, set by per-occurrence cancellation AND the end-membership cascade), or the
  // member's pending membership end. The calendar honours cancelledFrom/until and stops occurrences,
  // so the view must too — otherwise a bounded series shows "no end date" while the calendar correctly
  // stops it (the pending-termination bug). Treat cancelledFrom as an `until` one instant before that
  // day, and use whichever bound falls earliest.
  const rawUntil = recurrence.until ? new Date(recurrence.until) : null;
  const cancelledFromRaw = booking.recurrence?.cancelledFrom ?? booking.recurrencePattern?.cancelledFrom ?? null;
  const cancelledUntil = cancelledFromRaw
    ? new Date(utcDayBounds(new Date(cancelledFromRaw)).start.getTime() - 1)
    : null;
  const effectiveUntil = earliestDate(rawUntil, cancelledUntil, membershipUntil);

  if (recurrence.count == null && effectiveUntil == null) return null; // genuinely unbounded — no end

  const source = booking.recurrence ?? booking.recurrencePattern;
  const bounded: Booking =
    effectiveUntil && source ? { ...booking, recurrence: { ...source, until: effectiveUntil } } : booking;
  const occurrences = expandRecurrence(bounded);
  if (occurrences.length === 0) return null;
  return occurrences[occurrences.length - 1];
}

/** The earliest non-null date among the arguments (null when all are null). */
function earliestDate(...dates: Array<Date | null>): Date | null {
  return dates.reduce<Date | null>((acc, d) => (d && (!acc || d < acc) ? d : acc), null);
}

/**
 * The last occurrence of a series on or before `until` (inclusive), skipping excluded dates. Used to
 * bound indefinite series (notably slots, whose recurrence.until is stripped for R13) by an external
 * cap such as a member's membership end date.
 */
function lastOccurrenceOnOrBefore(booking: Booking, recurrence: Recurrence, until: Date): Date | null {
  const rule = buildRule(booking, recurrence);
  const exceptions = recurrence.exceptions?.map((d) => utcDayKey(new Date(d))) ?? [];
  let candidate = rule.before(until, true);
  while (candidate && exceptions.includes(utcDayKey(candidate))) {
    candidate = rule.before(candidate, false);
  }
  return candidate ?? null;
}

/**
 * Whether a booking has any occurrence whose end-time is still in the future (time-of-day aware,
 * so today's already-finished occurrence doesn't count). Uses `rule.after` rather than
 * `expandRecurrence`, so it stays correct for unbounded series without the 1-year horizon cap.
 * Non-recurring bookings test their own single date+endTime.
 */
export function hasFutureOccurrence(booking: Booking, now: Date = new Date()): boolean {
  const endHours = parseInt(booking.endTime.slice(0, 2), 10);
  const endMinutes = parseInt(booking.endTime.slice(2, 4), 10);
  // Build the occurrence's end INSTANT on `day`'s UTC calendar day at the booking's end time.
  // `day` is always a UTC-day-anchored Date (booking.date, or an rrule candidate derived from a
  // UTC dtstart) — Date.UTC keeps the day fixed regardless of viewer TZ, unlike the previous
  // local `setHours`, which reinterpreted the UTC day in browser-local time (see
  // src/utils/bookingActiveness.ts's endOfBookingDay for the same pattern).
  const withEndTime = (day: Date): Date =>
    new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), endHours, endMinutes, 0, 0));

  const recurrence = getEffectiveRecurrence(booking);
  if (!recurrence) {
    const bookingDate = booking.date instanceof Date ? booking.date : new Date(booking.date);
    return withEndTime(bookingDate) > now;
  }

  const rule = buildRule(booking, recurrence);
  const exceptions = recurrence.exceptions?.map((d) => utcDayKey(new Date(d))) ?? [];
  // "Today" is the centre's (UTC) calendar day, not the viewer's local day — a Rome viewer at
  // 00:30 local must see the same centre-day state as a London viewer. Bounds the scan to
  // today-onward in UTC before the time-of-day `withEndTime` check below.
  let candidate = rule.after(utcDayBounds(now).start, true);
  while (candidate) {
    if (exceptions.includes(utcDayKey(candidate))) {
      candidate = rule.after(candidate, false);
      continue;
    }
    if (withEndTime(candidate) > now) return true;
    candidate = rule.after(candidate, false);
  }
  return false;
}

/**
 * The booking is entirely in the past: it has no future occurrence and is not a slot (slots run
 * indefinitely by R13, so they're never "ended"). Drives the "This booking has ended and is in
 * the past." banner.
 */
export function isBookingFullyInPast(booking: Booking, now: Date = new Date()): boolean {
  if (booking.type === "slot") return false;
  return !hasFutureOccurrence(booking, now);
}

export function occursOnDate(booking: Booking, date: Date): boolean {
  const recurrence = getEffectiveRecurrence(booking);
  if (!recurrence) {
    return sameUtcDay(new Date(booking.date), date);
  }

  const { start: dayStart, end: dayEnd } = utcDayBounds(date);
  // Use buildRuleForDateCheck (UTC-midnight dtStart) rather than buildRule (UTC dtStart offset by
  // the booking's startTime) — this function only needs day-identity, so anchoring exactly at UTC
  // midnight keeps the `between(dayStart, dayEnd, true)` window unambiguous. See buildRuleForDateCheck.
  const occurences = buildRuleForDateCheck(booking, recurrence).between(dayStart, dayEnd, true);
  const exceptions = recurrence.exceptions?.map((d) => utcDayKey(new Date(d))) ?? [];
  return occurences.some((occ) => !exceptions.includes(utcDayKey(occ)));
}

/**
 * Collapse base/instance documents of the same recurring series into a single entry, preferring
 * the base (the document carrying `recurrence`). Standalone (non-recurring) bookings pass through
 * untouched.
 *
 * Single source of truth for "one row per series": the Your Bookings list and the dashboard's
 * "you have N bookings today" count both call this, so the count can never diverge from the list.
 */
export function dedupeBookingsBySeries(bookings: Booking[]): Booking[] {
  const baseBySeries = new Map<string, Booking>();
  const standalone: Booking[] = [];
  for (const booking of bookings) {
    const recurrence = booking.recurrence || booking.recurrencePattern;
    if (recurrence) {
      const baseId = recurrence.baseBookingId || booking.parentBookingId || booking.id;
      const existing = baseBySeries.get(baseId);
      // Prefer the base document (has `recurrence`) over an instance (has `recurrencePattern`).
      if (!existing || (booking.recurrence && !existing.recurrence)) {
        baseBySeries.set(baseId, booking);
      }
    } else {
      standalone.push(booking);
    }
  }
  return [...baseBySeries.values(), ...standalone];
}

export function expandedRecurrenceInRange(booking: Booking, start: Date, end: Date): string[] {
  const recurrence = getEffectiveRecurrence(booking);
  if (!recurrence) {
    const bookingDate = booking.date instanceof Date ? booking.date : new Date(booking.date);
    return [utcDayKey(bookingDate)];
  }
  // Use buildRuleForDateCheck (UTC-midnight dtStart) rather than buildRule (UTC dtStart offset by
  // the booking's startTime) — the caller only wants day-identity strings (utcDayKey below), never
  // the exact instant. See buildRuleForDateCheck's docblock.
  const occurrences = buildRuleForDateCheck(booking, recurrence).between(start, end, true);
  const exceptions = recurrence.exceptions?.map((d) => utcDayKey(new Date(d))) ?? [];
  return occurrences.filter((d) => !exceptions.includes(utcDayKey(d))).map((d) => utcDayKey(d));
}

/**
 * Returns the next occurrence whose end-time hasn't passed yet for a recurring booking, or the
 * booking's own date for non-recurring bookings. Time-of-day aware: today's occurrence rolls
 * forward to the next one once its endTime is in the past, so a "Today at 07:00–08:00" weekly
 * booking starts reading as next Tuesday from 08:00 onwards.
 *
 * Handles both parent bookings (with `recurrence`) and instance bookings (with
 * `recurrencePattern`) — either can carry the genesis date and need next-occurrence computation.
 */
export function getNextOccurrenceDate(booking: Booking, now: Date = new Date()): Date {
  const bookingDate = booking.date instanceof Date ? booking.date : new Date(booking.date);

  // The slot fallback inside getEffectiveRecurrence covers the legacy/sync edge cases where
  // a slot booking has no explicit recurrence document — see its docblock for the bug history.
  const recurrence = getEffectiveRecurrence(booking);
  if (!recurrence) {
    return bookingDate;
  }

  const rule = buildRule(booking, recurrence);

  // "Today" is the centre's (UTC) calendar day, not the viewer's local day — a Rome viewer at
  // 00:30 local must see the same centre-day state as a London viewer. Bounds the scan to
  // today-onward in UTC.
  const today = utcDayBounds(now).start;
  const exceptions = recurrence.exceptions?.map((d) => utcDayKey(new Date(d))) ?? [];
  const endHours = parseInt(booking.endTime.slice(0, 2), 10);
  const endMinutes = parseInt(booking.endTime.slice(2, 4), 10);

  // Build the occurrence's end INSTANT on the candidate's UTC calendar day at the booking's end
  // time. `candidate` is always a UTC-day-anchored rrule result (see buildRule's UTC dtstart) —
  // Date.UTC keeps the day fixed regardless of viewer TZ, unlike the previous local `setHours`,
  // which reinterpreted the UTC day in browser-local time (see
  // src/utils/bookingActiveness.ts's endOfBookingDay for the same pattern).
  const endOfBookingDay = (day: Date): Date =>
    new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), endHours, endMinutes, 0, 0));

  // Walk forward from today, skipping exclusions AND any candidate whose end-datetime is at or
  // before "now" (i.e. today's occurrence has already finished).
  let candidate = rule.after(today, true);
  while (candidate) {
    if (exceptions.includes(utcDayKey(candidate))) {
      candidate = rule.after(candidate, false);
      continue;
    }
    if (endOfBookingDay(candidate) <= now) {
      candidate = rule.after(candidate, false);
      continue;
    }
    return candidate;
  }

  // No future occurrence — fall back to the genesis date so the caller can still render
  // something for inactive series.
  return bookingDate;
}

/**
 * Formats a booking's display date using the next occurrence for recurring bookings.
 * Returns "Today at HH:MM" when the next occurrence is today.
 */
export function formatBookingDate(
  booking: Booking,
  options?: { withDayName?: boolean; withTime?: boolean },
): string {
  const nextDate = getNextOccurrenceDate(booking);
  const today = new Date();
  const formattedTime = `${booking.startTime.slice(0, 2)}:${booking.startTime.slice(2)}`;

  if (sameUtcDay(nextDate, today)) {
    return options?.withTime ? `Today at ${formattedTime}` : "Today";
  }

  const dateStr = formatUtcDate(nextDate, options?.withDayName);
  return options?.withTime ? `${dateStr} at ${formattedTime}` : dateStr;
}

function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
}

/**
 * Booking-aware date label for "Your bookings" / practice tables.
 *
 *  - If the booking's next occurrence is today AND `now` falls between its start- and end-time
 *    → returns "Now".
 *  - Otherwise delegates to `humaniseDateFriendly(getNextOccurrenceDate(booking, now), now)`
 *    so the caption reads "Today" / "Tomorrow" / "Wed, 22nd April" as appropriate.
 */
export function humaniseBookingDate(booking: Booking, now: Date = new Date()): string {
  const next = getNextOccurrenceDate(booking, now);
  const sameDay = sameUtcDay(next, now);
  if (sameDay) {
    const startMin =
      parseInt(booking.startTime.slice(0, 2), 10) * 60 + parseInt(booking.startTime.slice(2, 4), 10);
    const endMin =
      parseInt(booking.endTime.slice(0, 2), 10) * 60 + parseInt(booking.endTime.slice(2, 4), 10);
    // startMin/endMin are the centre's (UTC) minutes-of-day, so `now` must be read via its UTC
    // getters too — a local read would compare UTC start/end times against the viewer's local
    // clock, misfiring "Now" at any non-zero viewer offset.
    const nowMin = now.getUTCHours() * 60 + now.getUTCMinutes();
    if (nowMin >= startMin && nowMin < endMin) {
      return "Now";
    }
  }
  return humaniseDateFriendly(next, now);
}

/**
 * Formats a genesis date with ordinal suffix: "4th March 2025"
 */
export function formatGenesisDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  // UTC getters: a genesis date is stored as UTC-midnight of the centre's day, so browser-local
  // getters (getDate/getFullYear/toLocaleDateString without timeZone) can read the wrong calendar
  // day for a viewer west of UTC.
  const day = d.getUTCDate();
  const ordinal = getOrdinalSuffix(day);
  const month = new Intl.DateTimeFormat("en-GB", { month: "long", timeZone: "UTC" }).format(d);
  const year = d.getUTCFullYear();
  return `${day}${ordinal} ${month} ${year}`;
}
